import { useState, useEffect, useContext } from 'react';
import { UserStateContext } from 'context/user-state-context';
import { toasterService } from 'components/toaster/toaster-service';
import { createNote, deleteNote, getNotes, updateNote } from 'shared/common.api';

// Module-level so the default is stable: an inline `() => {}` default would be a new
// function every render and re-fire the fetch effect below in a loop.
const noop = () => {};

/**
 * Data + draft state for a record's notes, shared by `CinchNotes` and any surface that
 * needs the notes list inside its own chrome (e.g. the customer drawer's Summary tab).
 *
 * @param {string} objectId - The ID of the object the notes belong to.
 * @param {string} type - The note type / table name (e.g. 'customer', 'task', 'schedule').
 * @param {function} onNotesChange - Called with the full list whenever it loads or changes.
 */
export function useCinchNotes({
  objectId,
  type,
  onNotesChange = noop
}) {
  const {
    asCompany
  } = useContext(UserStateContext);
  const [notes, setNotes] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [isAddingNewNote, setIsAddingNewNote] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');

  // Drop the previous record's notes and drafts so switching records (e.g. opening another
  // customer in the drawer) never shows stale notes or carries a half-typed note across.
  useEffect(() => {
    setNotes(null);
    setEditingNoteId(null);
    setEditingText('');
    setIsAddingNewNote(false);
    setNewNoteText('');
  }, [objectId, type]);
  useEffect(() => {
    // Ignore a late response for a record the user has already navigated away from.
    let cancelled = false;
    if (objectId && type) {
      getNotes({
        company: asCompany.id,
        object_id: objectId,
        type: type
      }).then(notesData => {
        if (cancelled) return;
        if (notesData?.data?.results) {
          setNotes(notesData.data.results);
          onNotesChange(notesData.data.results);
        } else {
          setNotes([]);
        }
      }).catch(error => {
        if (cancelled) return;
        console.error('Error fetching notes:', error);
        setNotes([]);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [objectId, type, asCompany.id, onNotesChange]);
  const startAdding = () => setIsAddingNewNote(true);
  const cancelAdding = () => {
    setIsAddingNewNote(false);
    setNewNoteText('');
  };
  const startEditing = note => {
    setEditingNoteId(note.id);
    setEditingText(note.note);
  };
  const cancelEditing = () => {
    setEditingNoteId(null);
    setEditingText('');
  };
  const handleCreateNote = async () => {
    try {
      const noteData = await createNote({
        company: asCompany.id,
        object_id: objectId,
        type: type,
        note: newNoteText
      });
      const updatedNotes = notes && Array.isArray(notes) ? [noteData.data, ...notes] : [noteData.data];
      setNotes(updatedNotes);
      onNotesChange(updatedNotes);
      cancelAdding();
    } catch (error) {
      console.error('Error creating note:', error);
      toasterService.error('Unable to save the note.');
    }
  };
  const handleUpdateNote = async noteId => {
    try {
      const updatedNote = await updateNote(noteId, {
        note: editingText,
        object_id: objectId,
        type: type,
        company: asCompany.id
      });
      const updatedNotes = notes.map(n => n.id === noteId ? updatedNote.data : n);
      setNotes(updatedNotes);
      onNotesChange(updatedNotes);
      cancelEditing();
    } catch (error) {
      console.error('Error updating note:', error);
      toasterService.error('Unable to update the note.');
    }
  };
  const handleDeleteNote = async noteId => {
    try {
      await deleteNote(noteId, {
        note: '',
        object_id: objectId,
        type: type,
        company: asCompany.id
      });
      const updatedNotes = notes.filter(n => n.id !== noteId);
      setNotes(updatedNotes);
      onNotesChange(updatedNotes);
    } catch (error) {
      console.error('Error deleting note:', error);
      toasterService.error('Unable to delete the note.');
    }
  };
  return {
    notes,
    editingNoteId,
    editingText,
    setEditingText,
    isAddingNewNote,
    newNoteText,
    setNewNoteText,
    startAdding,
    cancelAdding,
    startEditing,
    cancelEditing,
    handleCreateNote,
    handleUpdateNote,
    handleDeleteNote
  };
}